import React, { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Spinner from '@atlaskit/spinner';
import styled from '@emotion/styled';
import EmailIcon from '@atlaskit/icon/glyph/email';
import { colors, gridSize } from '@atlaskit/theme';

import FieldGroup from '../../../components/FieldGroup';
import EmailContainer, { FieldViewType } from '../../../components/EmailContainer';
import PasswordContainer from '../../../components/PasswordContainer';
import TallButton from '../../../components/TallButton';
import FormError from '../../../components/FormError';
import FormInfo from '../../../components/FormInfo';
import { StyledSocialLoginButton } from '../../../components/SocialLoginPanel';
import messages from '../../../pages/login/components/LoginForm.messages';
import {
  LoginFieldError,
  LoginGlobalInfo,
  CheckUserNameRedirectType,
  UsernameV2CheckResponse,
} from '../../../types';
import { Application, applications } from '../../../utilities/applications';
import { useAnalyticsClient } from '../../../utilities/analytics/analytics-web-client';
import { loginPageId } from '../../../pages/login/constants';
import { LoginRecaptcha } from '../../../pages/login/components/LoginRecaptcha';
import { useLoginTooManyAttemptsRecaptchaContext } from '../../../pages/login/contexts/LoginTooManyAttemptsRecaptchaContext';
import { LoginMarketingConsent } from '../../../pages/login/components/MarketingConsent';
import { LoginGlobalErrorWithMessage } from '../../../pages/login/hooks/useLogin';
import { useTranslateFormErrorIfAvailable } from '../../../pages/signup/FormValidation.messages';
import { Checkbox } from '@atlaskit/checkbox';
import { Box, Flex } from '@atlaskit/primitives';
import InlineMessage from '@atlaskit/inline-message';
import AccountEnvSelector, { AccountEnvOption } from './AccountEnvSelector';
import { useNavigateSoftOrHard } from '../../../utilities/hooks/useNavigateSoftOrHard';

export const OpeningSso = styled.div`
  align-items: center;
  color: ${colors.N90};
  display: flex;
  height: 40px;
  justify-content: space-between;
  padding: 0 8px;
`;

export const Form = styled.form`
  margin: 0;
`;

const InputsWrapper = styled.div`
  margin-bottom: ${gridSize()}px;
`;

interface ValidationErrors {
  email?: string;
  password?: string;
}

const getEmailFieldViewType = (
  restrictedEmail,
  redirectType,
  isSecondStep,
  isThirdStep,
  isLoading
) => {
  if (restrictedEmail || redirectType === CheckUserNameRedirectType.Sso) {
    return FieldViewType.Restricted;
  } else if (isThirdStep || isLoading || isSecondStep) {
    return FieldViewType.ReadOnlyWithEditButton;
  }
  return FieldViewType.Editable;
};

export type LoginFormProps = {
  application: Application | null;
  redirectType: CheckUserNameRedirectType | null;
  isRedirecting?: boolean;
  isSecondStep?: boolean;
  isPasswordlessAllowed?: boolean;
  isLoading?: boolean;
  isCheckUsernameLoading?: boolean;
  onContinue: (username: string) => void;
  onLogIn: (email: string, password: string) => void;
  onPasswordlessLogIn: (email: string) => void;
  fieldErrors: LoginFieldError;
  email: string;
  error: LoginGlobalErrorWithMessage | null | undefined;
  info: LoginGlobalInfo | null | undefined;
  onEditEmail: () => void;
  onValidationError: (errors: ValidationErrors) => void;
  onFormClick: () => void;
  restrictedEmail: boolean;
  pageId: string;
  onEmailChange: (username: string) => void;
  showRememberMeCheckbox: boolean;
  rememberMeChecked: boolean;
  onToggleRememberMeCheckbox: () => void;
  v2Accounts: UsernameV2CheckResponse;
  isNewV2CheckUsernameFlow: boolean;
};

export const LoginForm = ({
  application,
  email,
  error,
  fieldErrors,
  info,
  isLoading,
  isPasswordlessAllowed,
  isRedirecting,
  isSecondStep,
  isCheckUsernameLoading,
  onContinue,
  onEditEmail,
  onEmailChange,
  onFormClick,
  onLogIn,
  onPasswordlessLogIn,
  onValidationError,
  pageId,
  redirectType,
  restrictedEmail,
  showRememberMeCheckbox,
  rememberMeChecked,
  onToggleRememberMeCheckbox,
  v2Accounts,
  isNewV2CheckUsernameFlow,
}: LoginFormProps) => {
  const intl = useIntl();
  const navigate = useNavigateSoftOrHard();
  const { formatMessage } = intl;
  const translatedEmailFieldError = useTranslateFormErrorIfAvailable(fieldErrors.email);
  const analyticsClient = useAnalyticsClient();
  const [password, setPassword] = useState<string>('');
  const [accountEnv, setAccountEnv] = useState<string>('');
  const [buttonClicked, setButtonClicked] = useState<'submit' | 'passwordless'>('submit');
  const inputRef = useRef<HTMLInputElement>(null);
  const [passwordFieldError, setPasswordFieldError] = useState<string | null>(null);
  const [accountEnvFieldError, setAccountEnvFieldError] = useState<string | undefined>(undefined);
  const [isThirdStep, setIsThirdStep] = useState<boolean>(false);

  const onAccountEnvChange = useCallback((value: string) => {
    setAccountEnv(value);
    setAccountEnvFieldError(undefined);
  }, []);

  const onEmailClick = useCallback(() => {
    setPassword('');
    setPasswordFieldError(null);
    setAccountEnv('');
    setAccountEnvFieldError(undefined);
    setIsThirdStep(false);
    onEditEmail();
  }, [onEditEmail]);

  const { captchaValue, isRequired: isTooManyAttemptsRecaptchaRequired } =
    useLoginTooManyAttemptsRecaptchaContext();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const trimmedEmail = email.trim();
    const emailEmpty = trimmedEmail === '';
    const emailInvalid = !trimmedEmail.match(/.+@.+/);
    const passwordEmpty = password === '';
    const accountEnvEmpty = accountEnv === '';

    // Ignore submission of form while the app is loading.
    if (isLoading) {
      return;
    }

    setButtonClicked('submit');
    analyticsClient.buttonClickedEvent(pageId, isThirdStep ? 'login' : 'continueToLogin');

    if (emailEmpty) {
      inputRef.current?.focus();
      const emailValidationError = formatMessage(messages.emailEmpty);
      analyticsClient.errorShownEvent(loginPageId, 'validationError');
      onValidationError({ email: emailValidationError });
      return;
    }

    if (emailInvalid) {
      inputRef.current?.focus();
      const emailValidationError = formatMessage(messages.emailInvalid);
      analyticsClient.errorShownEvent(loginPageId, 'validationError');
      onValidationError({ email: emailValidationError });
      return;
    }

    if (isThirdStep && passwordEmpty) {
      setPasswordFieldError(formatMessage(messages.passwordEmpty));
      return;
    }

    if (isSecondStep && accountEnvEmpty) {
      setAccountEnvFieldError(formatMessage(messages.unexpectedErrorHappened));
      return;
    }

    if (!isSecondStep && !isThirdStep) {
      onContinue(trimmedEmail);
    } else if (isSecondStep && !isThirdStep && accountEnv === 'atlassian-cloud') {
      setIsThirdStep(true);
      return;
    } else if (
      isSecondStep &&
      !isThirdStep &&
      !accountEnvEmpty &&
      accountEnv !== 'atlassian-cloud'
    ) {
      // Redirect to the selected IC environment
      const selected = v2Accounts?.find(
        acc => acc.action === 'ic_redirect' && acc.ic_id === accountEnv
      );
      if (selected && selected.action === 'ic_redirect') {
        navigate(selected.redirect_uri);
      }
      return;
    } else if (isThirdStep && !accountEnvEmpty && accountEnv === 'atlassian-cloud') {
      onLogIn(trimmedEmail, password);
    }
  };

  const handlePasswordlessButtonClick = async () => {
    const trimmedEmail = email.trim();

    if (isLoading || !trimmedEmail) {
      return;
    }

    setButtonClicked('passwordless');
    onPasswordlessLogIn(trimmedEmail);
  };

  const handlePasswordChange = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      if (passwordFieldError) {
        setPasswordFieldError(null);
      }

      setPassword(e.currentTarget.value);
    },
    [passwordFieldError, setPasswordFieldError, setPassword]
  );

  const buttonContent = () => {
    if (!isThirdStep) {
      return <FormattedMessage id="login.form.continue" defaultMessage="Continue" />;
    }
    if (isPasswordlessAllowed) {
      return (
        <FormattedMessage id="common.login.with.password" defaultMessage="Log in with password" />
      );
    }
    return <FormattedMessage id="common.login" defaultMessage="Log in" />;
  };

  const infoContent = () => {
    const { formatMessage } = intl;
    const applicationName = application ? applications[application].name : 'Product';
    const testId = 'login-info-test-id';

    if (info) {
      switch (info.code) {
        case 'migratingExistingUser': {
          const summary = formatMessage(messages.migrateAccountInfo, {
            application: applicationName,
          });
          return (
            <FormInfo id="login-info" data-testid={testId}>
              {summary}
            </FormInfo>
          );
        }
        case 'existingUserSignupAttempt': {
          const summary = formatMessage(messages.existingUserSignupAttemptInfo);
          return (
            <FormInfo id="login-info" data-testid={testId}>
              {summary}
            </FormInfo>
          );
        }
        default:
          return (
            <FormInfo id="login-info" data-testid={testId} isHidden={true}>
              {' '}
            </FormInfo>
          );
      }
    }
  };
  // if not using the new V2 check username flow, allow the user to bypass the environment selection step
  useEffect(() => {
    if (!isNewV2CheckUsernameFlow && isSecondStep) {
      setAccountEnv('atlassian-cloud');
      setIsThirdStep(true);
    }
  }, [isNewV2CheckUsernameFlow, isSecondStep]);

  useEffect(() => {
    analyticsClient.stopInitialLoadApdexEvent('viewLoginPage');
  }, [analyticsClient]);

  const emailError = (): string | undefined => {
    switch (fieldErrors.email) {
      case 'unknown':
        return intl.formatMessage(messages.unexpectedErrorHappened);
      case 'rate_limit_exceeded_message':
        return intl.formatMessage(messages.rateLimitErrorMessage);
      default:
        return translatedEmailFieldError;
    }
  };

  const renderExperimentButtons = () => {
    if (redirectType === CheckUserNameRedirectType.Sso) {
      return (
        <OpeningSso>
          <FormattedMessage
            id="login.form.opening.sso"
            defaultMessage="Opening your single sign-on provider"
          />
          <Spinner />
        </OpeningSso>
      );
    } else {
      return (
        <TallButton
          appearance="primary"
          id="login-submit"
          testId="login-submit-idf-testid"
          type="submit"
          isFullWidth
          isDisabled={isThirdStep && isTooManyAttemptsRecaptchaRequired && !captchaValue}
          isLoading={
            buttonClicked === 'submit' && (!!isRedirecting || isLoading || isCheckUsernameLoading)
          }>
          {buttonContent()}
        </TallButton>
      );
    }
  };

  const options: AccountEnvOption[] = useMemo(() => {
    if (!v2Accounts || v2Accounts.length === 0) {
      return [];
    }
    return v2Accounts.map(account => {
      if (account.action === 'ic_redirect') {
        return {
          label: (
            <FormattedMessage
              id="isolated-cloud-label"
              defaultMessage="{name} Isolated Cloud"
              description="Label for selecting the Isolated Cloud environment"
              values={{ name: account.ic_display_name }}
            />
          ),
          value: account.ic_id,
        };
      }
      return {
        label: (
          <FormattedMessage
            id="atlassian-cloud-and-data-center-label"
            defaultMessage="Atlassian Cloud and Data Center"
            description="Label for selecting the Atlassian Cloud and Data Center environment"
          />
        ),
        value: 'atlassian-cloud',
      };
    });
  }, [v2Accounts]);

  return (
    <div>
      {error && !isLoading && <FormError>{error?.message}</FormError>}
      <span> {infoContent()} </span>
      <Form
        id="form-login"
        data-testid="form-login"
        onSubmit={handleSubmit}
        onClick={onFormClick}
        noValidate>
        <InputsWrapper>
          <EmailContainer
            fieldViewType={getEmailFieldViewType(
              restrictedEmail,
              redirectType,
              isThirdStep,
              isSecondStep,
              isLoading
            )}
            fieldError={emailError()}
            onChange={onEmailChange}
            value={email}
            onEmailClick={onEmailClick}
            placeholderOverride={formatMessage(messages.emailPlaceholder)}
            inputRef={inputRef}
          />
          <AccountEnvSelector
            isHidden={!isSecondStep || !isNewV2CheckUsernameFlow}
            fieldError={accountEnvFieldError}
            onSelectEnvChange={onAccountEnvChange}
            isRequired={isSecondStep && isNewV2CheckUsernameFlow}
            isInvalid={!!accountEnvFieldError}
            options={options}
          />
          <PasswordContainer
            isHidden={!isThirdStep}
            fieldError={passwordFieldError || undefined}
            onChange={handlePasswordChange}
            value={password}
            className={`field-group password-field ${isThirdStep ? 'expanded' : 'hidden'}`}
            isRequired={isThirdStep}
          />

          <LoginRecaptcha />
          <LoginMarketingConsent />

          {/* Don't render the checkbox during SSO sign in, since it's already in flight. */}
          {showRememberMeCheckbox && redirectType !== CheckUserNameRedirectType.Sso && (
            <Box paddingBlockStart="space.100" paddingBlockEnd="space.100">
              <Flex direction="row" gap="space.050" alignItems="center">
                <Checkbox
                  testId="remember-me-checkbox"
                  isChecked={rememberMeChecked}
                  onClick={onToggleRememberMeCheckbox}
                  name="remember"
                  label={<FormattedMessage {...messages.rememberMeCheckboxLabel} />}
                  // During the redirect process (and while loading), we want to disable the checkbox so that the user doesn't interact with the checkbox.
                  isDisabled={
                    buttonClicked === 'submit' &&
                    (!!isRedirecting || isLoading || isCheckUsernameLoading)
                  }
                />
                <InlineMessage
                  // TODO [KIRBY-8737]: once it is available, use prop spacing='compact'
                  // https://hello.jira.atlassian.cloud/browse/KIRBY-8737
                  appearance="info"
                  iconLabel={formatMessage(messages.rememberMeMoreInfoLabel)}
                  placement="right"
                  fallbackPlacements={['bottom']}>
                  <FormattedMessage {...messages.rememberMeTooltip} />
                </InlineMessage>
              </Flex>
            </Box>
          )}
        </InputsWrapper>
        {renderExperimentButtons()}
        <FieldGroup isHidden={!isThirdStep || !isPasswordlessAllowed}>
          <StyledSocialLoginButton
            id="passwordless-button"
            iconBefore={<EmailIcon label="email" />}
            iconAfter={<span style={{ width: 24 }} />} // to center text
            isFullWidth={true}
            advisoryEnabled={false}
            onClick={handlePasswordlessButtonClick}
            isLoading={buttonClicked === 'passwordless' && (!!isRedirecting || isLoading)}
            isDisabled={
              (isTooManyAttemptsRecaptchaRequired && !captchaValue) || isLoading || isRedirecting
            }>
            <FormattedMessage
              id="login.form.passwordless.button"
              defaultMessage="Log in with email"
            />
          </StyledSocialLoginButton>
        </FieldGroup>
      </Form>
    </div>
  );
};

export default LoginForm;
